import { ref, reactive, computed, onMounted } from 'vue';
import { useToast } from '@/composables/common/useToast';
import store from '@/store';
import {
    getCategorias,
    getEquipos,
    guardarDatos as guardarDatosService,
    guardarResponsables as guardarResponsablesService,
    getIndumentarias as getIndumentariasService,
    bloquearIndumentarias as bloquearIndumentariasService,
    guardarDescuentos as guardarDescuentosService,
    crearBase as crearBaseService,
    calcularPlazos as calcularPlazosService,
    borrarPlazos as borrarPlazosService,
} from '@/services/admin/bases/crearDeportista';

const toast = useToast();

/**
 * Convierte un objeto plano { fieldName: value } al formato array que espera
 * el backend: [{ name: 'fieldName', value: 'fieldValue' }].
 * Ej: { alias: 'Manchito', talla: 'M' } → [{ name: 'alias', value: 'Manchito' }, { name: 'talla', value: 'M' }]
 *     {} → []
 *     null/undefined → []
 */
function objectToFieldArray(obj) {
    if (!obj) return [];
    return Object.entries(obj).map(([name, value]) => ({
        name,
        value: value != null ? String(value) : '',
    }));
}

export const COUNTRY_CODES = [
    { value: '+34', label: '+34 ES' },
    { value: '+376', label: '+376 AD' },
    { value: '+351', label: '+351 PT' },
    { value: '+44', label: '+44 GB' },
    { value: '+33', label: '+33 FR' },
    { value: '+49', label: '+49 DE' },
    { value: '+39', label: '+39 IT' },
    { value: '+31', label: '+31 NL' },
    { value: '+32', label: '+32 BE' },
    { value: '+1', label: '+1 US' },
];

export const DEFAULT_PAIS = { id: 68, iso3: 'ESP', nombre: 'España' };

/**
 * useBaseDeportistaWizard — Orquestador base del wizard de gestión de deportistas.
 * Compartido por useCrearDeportista y useRenovarDeportista.
 *
 * @param {number} listaId — ID de la lista de abonados (desde ruta).
 * @param {object} options
 * @param {import('vue').ComputedRef<string[]>} options.steps — Computed con los nombres de los pasos (REQUERIDO).
 * @param {boolean} [options.autoSelectCategoria=false] — Auto-seleccionar si solo hay una categoría.
 * @returns {object} Estado y métodos compartidos del wizard.
 */
export function useBaseDeportistaWizard(listaId, { steps: stepsProp, autoSelectCategoria = false } = {}) {
    const steps = stepsProp || ref([]);

    // SETTINGS puede no haberse disparado aún en este worker/test si el
    // primer dispatch falló por timeout. Asegura que getTeatro está poblado
    // antes de que metodosPago (computed) se evalúe en el render del step
    // Confirmación. Fire-and-forget: el render se reevaluará reactivamente
    // cuando getTeatro se popule.
    const hashTeatro = typeof localStorage !== 'undefined'
        ? localStorage.getItem('hash_teatro')
        : null;
    store.dispatch('SETTINGS', hashTeatro ? { filters: { tid: hashTeatro } } : {})
        .catch(err => console.warn('[useBaseDeportistaWizard] SETTINGS fallback failed:', err.message));

    // ── Estado principal ──
    const currentStep = ref(1);
    const submitting = ref(false);
    const categoriasLoading = ref(false);
    const equiposLoading = ref(false);
    const error = ref(null);
    const sessionId = ref(null);
    const phoneValid = ref(false);
    const finalizado = ref(false);
    const erroresFinal = ref([]);
    const plazosData = ref(null);
    const plazosLoading = ref(false);
    const plazosErrorMessage = ref('');

    const categorias = ref([]);
    const equipos = ref([]);
    const indumentarias = ref([]);
    const indumentariasLoading = ref(false);
    const selectedCategoria = ref(null);

    // ── Formulario reactivo (todos los campos compartidos) ──
    const formData = reactive({
        nombre: '',
        apellidos: '',
        tipoDocumento: 'DNI',
        dni: '',
        fecha_nacimiento: '',
        fecha_caducidad_dni: '',
        telefono: '',
        codigo_pais: '+34',
        email: '',
        pais_de_nacimiento: { ...DEFAULT_PAIS },
        provincia_nacimiento_id: null,
        localidad_nacimiento_id: null,
        nacionalidad: 'Española',
        direccion: '',
        club_de_procedencia: '',
        categoria_base_id: null,
        equipo_id: null,
        lateralidad: null,
        // Si es renovación, aquí va el id del BaseClub antiguo (lo setea useRenovarDeportista).
        base_id_antiguo: null,
        fields_deportista: {},

        // Custom fields for responsables (keyed by field name, like the frontend)
        fields_responsable_1: {},
        fields_responsable_2: {},

        // Documentación (crear)
        documentos_id: [],
        foto_url: '',

        // Indumentarias
        camiseta_nombre: '',
        camiseta_numero: '',
        indumentariasElegidas: [],

        // Descuentos
        descuento_aplicado: 0,
        descuento_integro: 0,

        // Confirmación
        metodo_pago: null,
        plazoSelect: null,
        frecuenciaSelect: null,

        // Responsables
        responsable_1_nombre: '',
        responsable_1_apellidos: '',
        responsable_1_tipoDocumento: 'DNI',
        responsable_1_dni: '',
        responsable_1_telefono: '',
        responsable_1_callingCode: '+34',
        responsable_1_email: '',
        responsable_1_fecha_nacimiento: '',

        responsable_2_nombre: '',
        responsable_2_apellidos: '',
        responsable_2_tipoDocumento: '',
        responsable_2_dni: '',
        responsable_2_telefono: '',
        responsable_2_callingCode: '+34',
        responsable_2_email: '',
        responsable_2_fecha_nacimiento: '',

        observaciones: '',
    });

    // ── Loading compuesto ──
    const loading = computed(() => categoriasLoading.value || equiposLoading.value);

    // ── Importe total ──
    const importeTotal = computed(() => {
        let total = selectedCategoria.value?.precio || 0;
        const elegidas = formData.indumentariasElegidas || [];
        for (const item of elegidas) {
            if (!item.incluida) {
                total += parseFloat(item.precio_total || item.precio * item.unidades || 0);
            }
        }
        return total;
    });

    // ── Opciones de descuento ──
    const descuentoOptions = computed(() => {
        const raw = store.getters.getConfig?.descuento_aplicado || [];
        const mapped = raw.map(opt => ({ value: opt.valor, label: opt.label }));
        return [{ value: 0, label: 'Sin descuento' }, ...mapped];
    });

    // ── Métodos de pago ──
    const metodosPago = computed(() => {
        const teatro = store.getters.getTeatro;
        return (teatro?.metodos_pago_abonos_bases || []).filter(m => m.show_taquilla == 1);
    });

    // ── Custom fields configurables para esta lista de abonados ──
    //    Cada campo tiene un pivot (deportista / representante) que indica
    //    en qué paso(s) del wizard debe renderizarse. Se leen desde la
    //    misma fuente que usa el flujo público (`getListaAbonados`).
    const fieldsDeportistas = computed(() => {
        return store.getters.getListaAbonados?.fields_deportistas || [];
    });

    // ── Navegación ──
    function advanceStep() {
        const max = steps.value.length;
        if (currentStep.value < max) {
            currentStep.value = currentStep.value + 1;
        }
    }

    function prevStep() {
        if (currentStep.value > 1) {
            currentStep.value--;
        }
    }

    // ── API: Categorías ──
    async function fetchCategorias() {
        categoriasLoading.value = true;
        error.value = null;
        try {
            const res = await getCategorias(listaId);
            categorias.value = res.data.data ?? res.data ?? [];

            if (autoSelectCategoria && categorias.value.length === 1) {
                selectedCategoria.value = categorias.value[0];
                formData.categoria_base_id = categorias.value[0].id;
            }
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error(err);
            }
            error.value = err;
            toast.error('Error al cargar las categorías');
        } finally {
            categoriasLoading.value = false;
        }
    }

    // ── API: Equipos ──
    async function fetchEquipos() {
        equiposLoading.value = true;
        try {
            const res = await getEquipos(listaId);
            equipos.value = res.data.data ?? res.data ?? [];
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error(err);
            }
        } finally {
            equiposLoading.value = false;
        }
    }

    // ── Watch: cambio de categoría ──
    function onCategoriaChange(cat) {
        selectedCategoria.value = cat;
        formData.categoria_base_id = cat?.id ?? null;
    }

    // ── API: Fetch indumentarias ──
    async function fetchIndumentarias(categoriaId) {
        indumentariasLoading.value = true;
        error.value = null;
        try {
            const res = await getIndumentariasService({
                lista_id: listaId,
                categoria_id: categoriaId,
            });
            indumentarias.value = res.data.indumentarias || [];
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error(err);
            }
            error.value = err;
            toast.error('Error al cargar las indumentarias');
        } finally {
            indumentariasLoading.value = false;
        }
    }

    // ── API: Guardar datos personales ──
    async function guardarDatos() {
        submitting.value = true;
        error.value = null;

        const payload = {
            lista_id: listaId,
            base: {
                nombre: formData.nombre,
                apellidos: formData.apellidos,
                tipoDocumento: formData.tipoDocumento,
                dni: formData.dni,
                fecha_nacimiento: formData.fecha_nacimiento,
                fecha_caducidad_dni: formData.fecha_caducidad_dni,
                telefono: formData.telefono,
                codigo_pais: formData.codigo_pais,
                email: formData.email,
                pais_de_nacimiento: formData.pais_de_nacimiento,
                pais_nacimiento_id: formData.pais_de_nacimiento?.id,
                provincia_nacimiento_id: formData.provincia_nacimiento_id,
                localidad_nacimiento_id: formData.localidad_nacimiento_id,
                nacionalidad: formData.nacionalidad,
                direccion: formData.direccion,
                club_de_procedencia: formData.club_de_procedencia,
                categoria_base_id: formData.categoria_base_id,
                equipo_id: formData.equipo_id,
                lateralidad: formData.lateralidad,
                taquilla: true,
                fields_deportista: objectToFieldArray(formData.fields_deportista),
            },
        };

        // Si es renovación, enviar base_id_antiguo para que el backend
        // copie lesiones/valoración del BaseClub anterior (BaseClub.php:334).
        if (formData.base_id_antiguo) {
            payload.base.base_id_antiguo = formData.base_id_antiguo;
        }

        try {
            const res = await guardarDatosService(payload);
            sessionId.value = res.data.session_id;
            advanceStep();
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error(err);
            }
            error.value = err;
            toast.error('Error al guardar los datos del deportista');
        } finally {
            submitting.value = false;
        }
    }

    // ── API: Guardar responsables ──
    async function guardarResponsables() {
        submitting.value = true;
        error.value = null;

        const responsable_1 = {
            nombre: formData.responsable_1_nombre,
            apellidos: formData.responsable_1_apellidos,
            dni: formData.responsable_1_dni,
            telefono: formData.responsable_1_telefono,
            email: formData.responsable_1_email,
            fecha_nacimiento: formData.responsable_1_fecha_nacimiento,
            fields: objectToFieldArray(formData.fields_responsable_1),
        };

        const payload = {
            responsable_1,
            lista_id: listaId,
            session_id: sessionId.value,
            observaciones: formData.observaciones,
            filters: {},
        };

        const tieneR2 = !!(
            (formData.responsable_2_nombre && formData.responsable_2_nombre.trim()) ||
            (formData.responsable_2_apellidos && formData.responsable_2_apellidos.trim()) ||
            (formData.responsable_2_dni && formData.responsable_2_dni.trim()) ||
            (formData.responsable_2_telefono && formData.responsable_2_telefono.trim()) ||
            (formData.responsable_2_email && formData.responsable_2_email.trim()) ||
            (formData.responsable_2_fecha_nacimiento && formData.responsable_2_fecha_nacimiento.trim())
        );

        if (tieneR2) {
            payload.responsable_2 = {
                nombre: formData.responsable_2_nombre,
                apellidos: formData.responsable_2_apellidos,
                dni: formData.responsable_2_dni,
                telefono: formData.responsable_2_telefono,
                email: formData.responsable_2_email,
                fecha_nacimiento: formData.responsable_2_fecha_nacimiento,
                fields: objectToFieldArray(formData.fields_responsable_2),
            };
        }

        try {
            await guardarResponsablesService(payload);
            advanceStep();
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error(err);
            }
            error.value = err;
            toast.error('Error al guardar los datos de los responsables');
        } finally {
            submitting.value = false;
        }
    }

    // ── API: Guardar indumentarias ──
    async function guardarIndumentarias(indumentariasData, campoNombre, campoNumero) {
        submitting.value = true;
        error.value = null;

        formData.camiseta_nombre = campoNombre || '';
        formData.camiseta_numero = campoNumero || '';
        formData.indumentariasElegidas = indumentariasData || [];

        if (!indumentariasData || !indumentariasData.length) {
            submitting.value = false;
            advanceStep();
            return;
        }

        const payload = {
            indumentarias: indumentariasData,
            session_id: sessionId.value,
            filters: {},
        };

        try {
            await bloquearIndumentariasService(payload);
            advanceStep();
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error(err);
            }
            error.value = err;
            const backendMsg = err?.response?.data?.message
                || err?.response?.data?.error
                || 'Error al reservar las indumentarias';
            toast.error(backendMsg);
        } finally {
            submitting.value = false;
        }
    }

    // ── API: Guardar descuentos ──
    async function guardarDescuentos(descuentoAplicado, descuentoIntegro) {
        submitting.value = true;
        error.value = null;

        formData.descuento_aplicado = descuentoAplicado || 0;
        formData.descuento_integro = descuentoIntegro || 0;

        const payload = {
            lista_id: listaId,
            session_id: sessionId.value,
            descuento_aplicado: formData.descuento_aplicado,
            descuento_integro: formData.descuento_integro,
        };

        try {
            await guardarDescuentosService(payload);
            advanceStep();
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error(err);
            }
            error.value = err;
            toast.error('Error al guardar el descuento');
        } finally {
            submitting.value = false;
        }
    }

    // ── API: Crear base (finalizar) ──
    async function crearBase(metodoPagoId) {
        submitting.value = true;
        error.value = null;
        erroresFinal.value = [];

        formData.metodo_pago = metodoPagoId;

        const payload = {
            lista_id: listaId,
            session_id: sessionId.value,
            metodo_pago: metodoPagoId,
            categoria_base_id: formData.categoria_base_id,
        };

        try {
            await crearBaseService(payload);
            finalizado.value = true;
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error(err);
            }
            error.value = err;
            erroresFinal.value = err?.response?.data || [];
            toast.error('Error al finalizar el proceso');
        } finally {
            submitting.value = false;
        }
    }

    // ── API: Calcular plazos ──
    async function calcularPlazos({ categoriaId, frecuenciaId, nPlazos }) {
        plazosLoading.value = true;
        error.value = null;
        plazosErrorMessage.value = '';
        try {
            const res = await calcularPlazosService({
                lista_id: listaId,
                session_id: sessionId.value,
                categoria_id: categoriaId,
                frecuencia_id: frecuenciaId,
                n_plazos: nPlazos,
                filters: {},
            });
            plazosData.value = res.data;
            formData.plazoSelect = nPlazos;
            formData.frecuenciaSelect = frecuenciaId;
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error(err);
            }
            error.value = err;
            const backendMsg = err?.response?.data?.message
                || err?.response?.data?.error
                || 'Error al calcular los plazos';
            plazosErrorMessage.value = backendMsg;
            toast.error(backendMsg);
        } finally {
            plazosLoading.value = false;
        }
    }

    // ── API: Borrar plazos ──
    async function borrarPlazos() {
        plazosLoading.value = true;
        error.value = null;
        plazosErrorMessage.value = '';
        try {
            const hashTeatro = localStorage.getItem('hash_teatro');
            await borrarPlazosService({
                lista_id: listaId,
                session_id: sessionId.value,
                filters: hashTeatro ? { tid: hashTeatro } : {},
            });
            plazosData.value = null;
            formData.plazoSelect = null;
            formData.frecuenciaSelect = null;
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error(err);
            }
            error.value = err;
            toast.error('Error al eliminar los plazos');
        } finally {
            plazosLoading.value = false;
        }
    }

    // ── Inicialización ──
    onMounted(() => {
        fetchCategorias();
        fetchEquipos();
    });

    // ── Helpers de encapsulación (usados por composables hijos) ──
    function setSubmitting(val) { submitting.value = val; }
    function setError(err) { error.value = err; }

    return {
        // Estado
        formData,
        currentStep,
        steps,
        loading,
        submitting,
        error,
        sessionId,
        phoneValid,
        categorias,
        categoriasLoading,
        equipos,
        equiposLoading,
        indumentarias,
        indumentariasLoading,
        selectedCategoria,
        finalizado,
        erroresFinal,
        plazosData,
        plazosLoading,
        plazosErrorMessage,

        // Computados
        importeTotal,
        descuentoOptions,
        metodosPago,
        fieldsDeportistas,

        // Navegación
        advanceStep,
        prevStep,

        // Encapsulación
        setSubmitting,
        setError,

        // API
        fetchCategorias,
        fetchEquipos,
        fetchIndumentarias,
        onCategoriaChange,
        guardarDatos,
        guardarResponsables,
        guardarIndumentarias,
        guardarDescuentos,
        crearBase,
        calcularPlazos,
        borrarPlazos,
    };
}
